import React from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Separator } from "../ui/separator";
import { cn } from "../../lib/utils";
import { resolveSocialIconSrc, formatLastActive } from "../utils";
import { useNow } from "../../hooks/useNow";
import { usePresenceLastOnline } from "../../context/UserRealtimeContext";

const StatRow = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3 text-sm">
    <span className="text-text-secondary">{label}</span>
    <span className="text-right font-medium text-text-primary">{value}</span>
  </div>
);

/**
 * @param {Object} props
 * @param {string} props.label
 * @param {string} props.url
 * @param {string} props.iconUrl
 * @param {string} [props.linkLabel]
 * @returns {JSX.Element}
 */
const LinkRow = ({ label, url, iconUrl, linkLabel }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center gap-3 rounded-lg border border-transparent p-3 transition-colors hover:border-ui-border hover:bg-ui-muted"
  >
    <span
      className="inline-flex size-10 shrink-0 items-center justify-center rounded-full border border-hero-glass-border bg-hero-glass-bg backdrop-blur-sm"
      aria-hidden
    >
      <img
        src={resolveSocialIconSrc(url, iconUrl)}
        alt=""
        width={20}
        height={20}
        className="size-6 object-contain"
        decoding="async"
      />
    </span>
    <div className="min-w-0 flex flex-col text-left">
      <span className="font-medium text-text-primary">{label}</span>
      <span className="truncate text-sm text-accent">{linkLabel || url}</span>
    </div>
  </a>
);

const blockMotion = (reduced) =>
  reduced
    ? {}
    : {
        initial: { opacity: 0, y: 8 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-32px" },
        transition: { duration: 0.24, ease: [0.25, 0.46, 0.45, 0.94] },
      };

/**
 * @param {Object} props
 * @param {Object} props.user
 * @param {boolean} props.isEditing
 * @param {boolean} [props.reduceMotion]
 * @returns {JSX.Element}
 */
const UserProfileSidebar = ({ user, isEditing, reduceMotion = false }) => {
  const links = user.links && Array.isArray(user.links) ? user.links : [];
  const now = useNow();
  const lastOnline = usePresenceLastOnline(user?.id, user?.last_online);

  const activityClass =
    user.activity_level === "high"
      ? "border-ui-border text-text-primary"
      : user.activity_level === "medium"
        ? "border-accent text-accent"
        : "border-ui-border text-text-secondary";

  return (
    <div className="space-y-4">
      <motion.div {...blockMotion(reduceMotion)}>
        <Card className="shadow-profile-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-text-primary">Quick stats</CardTitle>
            <Separator />
          </CardHeader>
          <CardContent className="space-y-3">
            <StatRow
              label="Member since"
              value={user.created_at ? new Date(user.created_at).toLocaleDateString() : "Unknown"}
            />
            <StatRow
              label="Last active"
              value={formatLastActive(lastOnline, now)}
            />
            <StatRow label="Connections" value="15" />
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="text-text-secondary">Activity</span>
              <Badge
                variant="outline"
                className={cn("font-normal capitalize", activityClass)}
              >
                {user.activity_level || "Low"}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {!isEditing && links.length > 0 && (
        <motion.div {...blockMotion(reduceMotion)}>
          <Card className="shadow-profile-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-text-primary">Profile links</CardTitle>
              <Separator />
            </CardHeader>
            <CardContent className="space-y-0 divide-y divide-ui-border p-0">
              <div className="space-y-0 p-2">
                {links.map((link, index) => (
                  <LinkRow
                    key={link.url || `link-${index}`}
                    label={link.label}
                    url={link.url}
                    iconUrl={link.icon_url}
                    linkLabel={link.link_label}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
};

/**
 * @param {Object} props
 * @param {Object} props.organization
 * @param {boolean} props.isEditing
 * @param {boolean} [props.reduceMotion]
 * @returns {JSX.Element}
 */
const OrganizationProfileSidebar = ({ organization, isEditing, reduceMotion = false }) => {
  const orgLinks =
    organization?.links && Array.isArray(organization.links) ? organization.links : [];

  return (
    <div className="space-y-4">
      <motion.div {...blockMotion(reduceMotion)}>
        <Card className="shadow-profile-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-text-primary">Quick stats</CardTitle>
            <Separator />
          </CardHeader>
          <CardContent className="space-y-3">
            <StatRow
              label="Created"
              value={organization.created_at ? new Date(organization.created_at).toLocaleDateString() : "Unknown"}
            />
            <StatRow label="Staff" value={organization.affiliation_count || 0} />
          </CardContent>
        </Card>
      </motion.div>

      {!isEditing && orgLinks.length > 0 && (
        <motion.div {...blockMotion(reduceMotion)}>
          <Card className="shadow-profile-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-text-primary">Profile links</CardTitle>
              <Separator />
            </CardHeader>
            <CardContent className="space-y-0 divide-y divide-ui-border p-0">
              <div className="space-y-0 p-2">
                {orgLinks.map((link, index) => (
                  <LinkRow
                    key={link.url || `org-link-${index}`}
                    label={link.label}
                    url={link.url}
                    iconUrl={link.icon_url}
                    linkLabel={link.link_label}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
};

export { UserProfileSidebar, OrganizationProfileSidebar };
